import React, { useState, useEffect, useCallback } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    IconButton,
    useTheme,
    useMediaQuery,
    Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useAuth } from './chess/lichess/LichessAuth';
import { useDevice } from './devices/DeviceProvider';

interface ConnectionStatus {
    chessSite: boolean;
    chessBoard: boolean;
    updateTime: Date;
}

const StatusToolbar = () => {
    const theme = useTheme();
    const { me } = useAuth();
    const { connected } = useDevice();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [status, setStatus] = useState<ConnectionStatus>({
        chessSite: false,
        chessBoard: false,
        updateTime: new Date(),
    });

    const updateStatus = useCallback(() => {
        setStatus({
            chessSite: me ? true : false,
            chessBoard: connected,
            updateTime: new Date(),
        });
    }, [me, connected])

    useEffect(() => {
        updateStatus();
    }, [updateStatus, me]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            updateStatus();
        }, 60000);
        return () => clearInterval(intervalId); // cleanup on unmount
    }, [updateStatus]);

    const formatUpdateTime = (time: Date) =>
        time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

    return (
        <AppBar
            position={isMobile ? 'static' : 'relative'}
            color="primary"
            elevation={4}
            sx={{
                zIndex: theme.zIndex.drawer - 1,
            }}
        >
            <Toolbar
                sx={{
                    justifyContent: 'space-between',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    gap: isMobile ? 2 : 0,
                    py: isMobile ? 1 : 0,
                }}
            >
                <Box
                    display="flex"
                    gap={isMobile ? 2 : 6}
                    alignItems={isMobile ? 'flex-start' : 'center'}
                    flexDirection={isMobile ? 'column' : 'row'}
                    width={isMobile ? '100%' : 'auto'}
                >
                    {/* Chess Board Status */}
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'bold' }}>
                            Chess Board:
                        </Typography>
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: status.chessBoard ? 'success.main' : 'error.main',
                            }}
                        />
                        <Typography
                            component="span"
                            color={status.chessBoard ? 'success.main' : 'error.main'}
                            sx={{ fontWeight: 'medium' }}
                        >
                            {status.chessBoard ? 'Connected' : 'Disconnected'}
                        </Typography>

                        {!status.chessBoard && (
                            <Tooltip title="Connect Chess Board">
                                <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => navigate('/scanner')}
                                >
                                    <LinkOffIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>

                    {/* Chess Site Status */}
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'bold' }}>
                            Chess Site:
                        </Typography>
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: status.chessSite ? 'success.main' : 'error.main',
                            }}
                        />
                        <Typography
                            component="span"
                            color={status.chessSite ? 'success.main' : 'error.main'}
                            sx={{ fontWeight: 'medium' }}
                        >
                            {status.chessSite ? 'Connected' : 'Disconnected'}
                        </Typography>

                        {!status.chessSite && (
                            <Tooltip title="Connect Chess Site">
                                <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => navigate('/connect')}
                                >
                                    <LinkOffIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Updated: {formatUpdateTime(status.updateTime)}
                    </Typography>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default StatusToolbar;
